import { Box, Center, Checkbox, Group, Menu, Stack, Tooltip, rem, useMantineTheme } from '@mantine/core';
import { GridApi, IFilterParams } from 'ag-grid-community';
import PriceOfferStatusIcon from 'components/tables/data/price-offer/PriceOfferStatusIcon';
import { PriceOfferRow } from 'components/tables/data/price-offer/types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { isEqual, noop } from 'lodash';
import P2Medium from 'components/typography/P2Medium';
import P2Regular from 'components/typography/P2Regular';

export const FILTER_KEY = 'status.priceOfferStatusId' as const;

const STATUSES = [1, 2, 3, 4, 999];
const ACTIVE_ICON_LIMIT = 4;

interface PriceOfferStatusFilterModel {
  filter: number[];
}

interface PriceOfferStatusFloatingFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
  parentFilterInstance?: (callback: (instance: unknown) => void) => void;
}

/**
 * Normalizes status filter values to numeric IDs.
 */
const normalizeValue = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(Number).filter((statusId) => !Number.isNaN(statusId));
};

/**
 * Parent status filter for the price-offer table.
 */
export const PriceOfferStatusColumnFilter = forwardRef(({ filterChangedCallback, colDef }: IFilterParams, ref) => {
  const value = useRef<number[]>([]);

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: PriceOfferRow }) {
      return value.current.includes(data.status.priceOfferStatusId);
    },

    getModel() {
      if (value.current.length === 0) {
        return null;
      }

      return { filter: value.current };
    },

    setModel(model: PriceOfferStatusFilterModel) {
      value.current = normalizeValue(model?.filter);
    },

    isFilterActive() {
      return value.current.length > 0;
    },

    onFloatingFilterChanged(newValue: number[]) {
      if (!isEqual(value.current, newValue)) {
        value.current = normalizeValue(newValue);
        filterChangedCallback();
      }
    },
  }));

  return <></>;
});

/**
 * Floating status filter for the price-offer table.
 */
export const PriceOfferStatusFloatingFilter = forwardRef(
  (
    { api, onChange, parentFilterInstance = noop }: PriceOfferStatusFloatingFilterProps,
    ref: React.Ref<{ onParentModelChanged: () => void }>
  ) => {
    const theme = useMantineTheme();
    const [value, setValue] = useState<number[]>(() => normalizeValue(api.getFilterModel()[FILTER_KEY]?.filter));

    useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));

    /**
     * Pushes the selected status ids into AG Grid or the table profile modal.
     */
    const pushModel = useCallback(
      (nextValue: number[]) => {
        if (onChange) {
          onChange({ [FILTER_KEY]: nextValue.length > 0 ? { filter: nextValue } : null });
          return;
        }

        parentFilterInstance((instance: unknown) => {
          (instance as { onFloatingFilterChanged: (value: number[]) => void }).onFloatingFilterChanged(nextValue);
        });
      },
      [onChange, parentFilterInstance]
    );

    /**
     * Toggles the status in the current filter value.
     */
    const toggleStatus = (statusId: number) => {
      const nextValue = value.includes(statusId)
        ? value.filter((currentStatusId) => currentStatusId !== statusId)
        : [...value, statusId];

      setValue(nextValue);
      pushModel(nextValue);
    };

    useEffect(() => {
      /**
       * Keeps local state in sync with profile/filter changes.
       */
      const handleModelUpdated = ({ api }: { api: GridApi }) => {
        setValue(normalizeValue(api.getFilterModel()[FILTER_KEY]?.filter));
      };

      api.addEventListener('modelUpdated', handleModelUpdated);
      return () => api.removeEventListener('modelUpdated', handleModelUpdated);
    }, [api]);

    return (
      <Menu withinPortal>
        <Menu.Target>
          <Box
            w="100%"
            h={32}
            py={4}
            px={8}
            bg="white"
            sx={{
              borderRadius: rem(4),
              border: `1px solid ${theme.colors.gray[4]}`,
              '&:hover': {
                borderColor: theme.colors.red[8],
                cursor: 'text',
              },
            }}
          >
            {value.length === 0 ? (
              <P2Regular color="gray.5">Všetky stavy</P2Regular>
            ) : (
              <Group noWrap spacing={4}>
                {value.slice(0, ACTIVE_ICON_LIMIT).map((statusId) => (
                  <Box key={statusId} sx={{ transform: 'translateY(-1px)' }}>
                    <PriceOfferStatusIcon iconOnly statusId={statusId} />
                  </Box>
                ))}
                {value.length > ACTIVE_ICON_LIMIT && (
                  <Tooltip
                    withinPortal
                    label={
                      <Stack spacing={8} py={4}>
                        {value.slice(ACTIVE_ICON_LIMIT).map((statusId) => (
                          <PriceOfferStatusIcon textColorOverride="white" key={statusId} statusId={statusId} />
                        ))}
                      </Stack>
                    }
                  >
                    <Center w={24} h={24} bg="gray.1" sx={{ transform: 'translateY(-1px)', borderRadius: '50%' }}>
                      <P2Medium color="gray.7">+{value.length - ACTIVE_ICON_LIMIT}</P2Medium>
                    </Center>
                  </Tooltip>
                )}
              </Group>
            )}
          </Box>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            <P2Regular color="gray.7">Vyberte stavy</P2Regular>
          </Menu.Label>
          {STATUSES.map((statusId) => (
            <Menu.Item key={statusId} onClick={() => toggleStatus(statusId)} closeMenuOnClick={false}>
              <Group spacing={16}>
                <Checkbox
                  styles={{ input: { cursor: 'pointer !important' } }}
                  checked={value.includes(statusId)}
                  onChange={() => toggleStatus(statusId)}
                />
                <PriceOfferStatusIcon statusId={statusId} />
              </Group>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }
);
